import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import PhoneInput from '../ui/PhoneInput';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { businessSectors, businessSubSectors, countries } from '../../data/businessSectors';
import { 
  User, 
  Building2, 
  Mail, 
  Phone, 
  ArrowLeft, 
  Check,
  AlertCircle,
  Star,
  CheckCircle,
  CreditCard,
  ShoppingCart
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { ServisfyLogo } from '../Common/ServisfyLogo';
import { useTiers } from '../../hooks/useTiers';
import TierPicker from './signup/TierPicker';
import { useLanguage } from '../../contexts/LanguageContext';

// D18-G: removed the hardcoded price table (was 29/99/299). Live pricing comes exclusively from useTiers
// (backend = the sole price authority, backed by commercial_plan_versions). No client-side price fallback.

const SignUp = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { toast } = useToast();
  const { registerPhoneNumber, verifyOTP, resendOTP } = useCRMAuth();
  const { login } = useAuth();
  const { tiers: liveTiers, byId: liveTiersById } = useTiers();
  // CUSTOMER WEB = NONE. The customer account tab and its OTP branch are removed;
  // business is the only web signup. Customers onboard in the Android/iOS Customer
  // apps, which run their own Firebase Phone Auth and never depended on this web
  // flow (it created no backend customer record -- only a client-side role label).
  const [activeTab, setActiveTab] = useState('business');
  const [trialPending, setTrialPending] = useState(false);

  // User signup state

  // Business signup state
  const [businessForm, setBusinessForm] = useState({
    // Required fields
    businessName: '',
    adminPhoneNumber: '',
    adminOtpCode: '',
    
    // Profile completion fields
    businessPhone: '',
    businessSector: '',
    businessSubSector: '',
    registrationNumber: '',
    registrationCountry: '',
    
    // Admin details
    adminName: '',
    adminEmail: '',
    
    // Subscription tier selection
    subscriptionTier: 'professional'
  });

  const [step, setStep] = useState(1); // 1: form, 2: otp verification, 3: success
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // OTP timer countdown
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Pricing-page deep link: preselect the plan chosen on public /pricing (/signup?plan=<tier_id>).
  // Runs once after the live tiers load, and is a strict no-op unless the param names a real
  // published tier — so the catalog (not the client) remains the sole price/version authority.
  const planDeepLinkApplied = React.useRef(false);
  useEffect(() => {
    if (planDeepLinkApplied.current) return;
    if (!liveTiers || !liveTiers.length) return;
    try {
      const wanted = new URLSearchParams(window.location.search).get('plan');
      if (wanted && liveTiersById && liveTiersById[wanted]) {
        setBusinessForm(prev => ({ ...prev, subscriptionTier: wanted }));
      }
    } catch (e) { /* ignore malformed query string */ }
    planDeepLinkApplied.current = true;
  }, [liveTiers, liveTiersById]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits && !value.startsWith('+')) {
      return '+' + digits;
    }
    return value;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const generateBrandId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleBusinessSignUp = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!businessForm.businessName || !businessForm.adminPhoneNumber || !businessForm.adminName) {
      toast({
        title: t('common.error'),
        description: t('signup.t.fillFields'),
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await registerPhoneNumber(businessForm.adminPhoneNumber);
      if (!result?.success) {
        toast({
          title: t('common.error'),
          description: result?.error || t('signup.t.sendFailed'),
          variant: "destructive",
        });
        return;
      }
      setStep(2);
      setOtpTimer(60);
      toast({
        title: t('signup.t.otpSentTitle'),
        description: t('signup.t.otpSentDesc', { phone: businessForm.adminPhoneNumber }),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('signup.t.sendFailed'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileCompletion = (e) => {
    e.preventDefault();
    
    if (!businessForm.businessPhone || !businessForm.businessSector || 
        !businessForm.businessSubSector || !businessForm.registrationNumber || 
        !businessForm.registrationCountry) {
      toast({
        title: t('common.error'),
        description: t('signup.t.completeProfileFields'),
        variant: "destructive",
      });
      return;
    }

    // Move to subscription tier selection
    setStep(4);
    toast({
      title: t('signup.t.profileCompleteTitle'),
      description: t('signup.t.profileCompleteDesc'),
    });
  };

  const handleStartTrial = async () => {
    if (!businessForm.businessName || !businessForm.adminName || !businessForm.adminPhoneNumber) {
      toast({ title: t('signup.t.missingInfo'), description: t('signup.t.completeBizFirst'), variant: 'destructive' });
      return;
    }
    setTrialPending(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/v1/billing/business/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: businessForm.businessName,
          admin_name: businessForm.adminName,
          admin_email: businessForm.adminEmail || `${businessForm.adminPhoneNumber.replace(/\D/g, '')}@servisfy.app`,
          admin_phone: businessForm.adminPhoneNumber,
          business_phone: businessForm.businessPhone,
          business_sector: businessForm.businessSector,
          business_sub_sector: businessForm.businessSubSector,
          registration_number: businessForm.registrationNumber,
          registration_country: businessForm.registrationCountry,
          tier_id: businessForm.subscriptionTier,
        })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not start trial');
      }
      const data = await resp.json();
      // Persist for the success screen
      localStorage.setItem('pending_signup', JSON.stringify({
        tenant_id: data.tenant_id,
        brand_id: data.brand_id,
        tier_id: data.tier_id,
        admin_name: businessForm.adminName,
        admin_phone: businessForm.adminPhoneNumber,
        business_name: businessForm.businessName,
        trial: true,
        trial_end_at: data.trial_end_at,
      }));
      // Enrich crm_user_data so BusinessContext can hydrate the tenant without requiring re-login
      const existingCrmUser = JSON.parse(localStorage.getItem('crm_user_data') || '{}');
      const enrichedUser = {
        ...existingCrmUser,
        brandId: data.brand_id,
        role: 'admin',
        name: businessForm.adminName,
        phone: businessForm.adminPhoneNumber,
        email: businessForm.adminEmail || `${businessForm.adminPhoneNumber.replace(/\D/g, '')}@servisfy.app`,
      };
      localStorage.setItem('crm_user_data', JSON.stringify(enrichedUser));
      login(enrichedUser);
      toast({ title: t('signup.t.trialStarted'), description: t('signup.t.trialStartedDesc') });
      navigate('/signup/success?trial=1');
    } catch (e) {
      toast({ title: t('signup.t.trialFailed'), description: e.message, variant: 'destructive' });
    } finally {
      setTrialPending(false);
    }
  };

  const handleSubscriptionSelection = async () => {
    const selectedTier = liveTiersById[businessForm.subscriptionTier] || liveTiers[0];
    if (!selectedTier) {
      toast({ title: t('common.error'), description: t('signup.t.selectTier'), variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      // 1. Register the business tenant on the backend
      const regResp = await fetch(`${backend}/api/v1/billing/business/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: businessForm.businessName,
          admin_name: businessForm.adminName,
          admin_email: businessForm.adminEmail || `${businessForm.adminPhoneNumber.replace(/\D/g, '')}@servisfy.app`,
          admin_phone: businessForm.adminPhoneNumber,
          business_phone: businessForm.businessPhone,
          business_sector: businessForm.businessSector,
          business_sub_sector: businessForm.businessSubSector,
          registration_number: businessForm.registrationNumber,
          registration_country: businessForm.registrationCountry,
          tier_id: businessForm.subscriptionTier,
        })
      });

      if (!regResp.ok) {
        const err = await regResp.json().catch(() => ({}));
        throw new Error(err.detail || 'Registration failed');
      }
      const regData = await regResp.json();

      // Persist pending signup so the success page can resume after payment
      localStorage.setItem('pending_signup', JSON.stringify({
        tenant_id: regData.tenant_id,
        brand_id: regData.brand_id,
        tier_id: regData.tier_id,
        admin_name: businessForm.adminName,
        admin_phone: businessForm.adminPhoneNumber,
        business_name: businessForm.businessName,
        session_id: regData.session_id,
      }));

      // Enrich crm_user_data so the by-phone rescue on login can find brand_id even
      // if staff_members write is delayed. window.location.href destroys React session,
      // so this must be written to localStorage before the redirect.
      const existingCrmUser = JSON.parse(localStorage.getItem('crm_user_data') || '{}');
      localStorage.setItem('crm_user_data', JSON.stringify({
        ...existingCrmUser,
        brandId: regData.brand_id,
        role: 'admin',
        name: businessForm.adminName,
        phone: businessForm.adminPhoneNumber,
        email: businessForm.adminEmail || `${businessForm.adminPhoneNumber.replace(/\D/g, '')}@servisfy.app`,
      }));

      // 2. Redirect to Stripe checkout
      if (regData.checkout_url) {
        window.location.href = regData.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast({
        title: t('signup.t.paymentError'),
        description: error.message || t('signup.t.paymentFailed'),
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  const completeRegistration = () => {
    // Generate Brand ID and complete registration
    const brandId = generateBrandId();
    
    toast({
      title: t('signup.regComplete'),
      description: `Welcome to ${(liveTiersById[businessForm.subscriptionTier] || {}).name || 'Servisfy'} plan! Your Brand ID is: ${brandId}`,
    });
    
    setStep(5);
  };

  const handleVerifyEmail = () => {
    setEmailVerified(true);
    toast({
      title: "Email Verified!",
      description: "Your email has been successfully verified",
    });
  };

  const handleVerifyPhone = () => {
    setPhoneVerified(true);
    toast({
      title: "Phone Verified!",
      description: "Your phone number has been successfully verified",
    });
  };

  // REMOVED (CUSTOMER WEB = NONE): handleUserOTPVerification minted a client-side
  // role:'customer' and navigated to /customer/dashboard — the discontinued Customer Web
  // plane. It created NO backend record (no customer-creation endpoint was called), and the
  // Android/iOS Customer apps run their own Firebase Phone Auth, so nothing depended on it.
  // No replacement web destination is provided, by design.

  const handleResendOTP = async () => {
    // Business is the only web signup; the customer branch is gone.
    const targetPhone = businessForm.adminPhoneNumber;
    const result = await resendOTP(targetPhone);
    setOtpTimer(60);
    if (result?.success) {
      toast({
        title: t('signup.t.otpSentTitle'),
        description: t('signup.t.otpResentDesc'),
      });
    } else {
      toast({
        title: t('signup.t.resendFailed'),
        description: result?.error || t('signup.t.sendFailed'),
        variant: "destructive",
      });
    }
  };

  const handleBusinessOTPVerification = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log('[SignUp] handleBusinessOTPVerification: calling verifyOTP, current step=', step);
      const result = await verifyOTP(businessForm.adminPhoneNumber, businessForm.adminOtpCode);
      console.log('[SignUp] verifyOTP returned:', result?.success, result?.error);
      if (!result?.success) {
        toast({
          title: t('signup.t.invalidOtp'),
          description: result?.error || t('signup.t.enterCorrect'),
          variant: "destructive",
        });
        return;
      }
      console.log('[SignUp] setStep(3) — advancing from OTP to profile completion');
      setStep(3);
      toast({
        title: t('signup.t.phoneVerified'),
        description: t('signup.t.phoneVerifiedBiz'),
      });
    } catch (error) {
      console.error('[SignUp] handleBusinessOTPVerification caught error:', error);
      toast({
        title: t('signup.t.verifyFailed'),
        description: t('signup.t.tryAgainSupport'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="absolute left-6 top-6"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('signup.backToLogin')}
            </Button>
            <ServisfyLogo variant="full" size="lg" />
          </div>
          <p className="text-xl text-gray-600">{t('signup.subtitle')}</p>
        </div>

        <Card className="backdrop-blur-sm bg-white/90 border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center">{t('signup.title')}</CardTitle>
            <CardDescription className="text-center">
              {t('signup.desc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-1">
                <TabsTrigger value="business" className="flex items-center space-x-2" data-testid="business-tab-trigger">
                  <Building2 className="h-4 w-4" />
                  <span>{t('signup.businessAccount')}</span>
                </TabsTrigger>
              </TabsList>

              {/* Business Registration */}
              <TabsContent value="business" className="space-y-6">
                {step === 1 && (
                  <form onSubmit={handleBusinessSignUp} className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">{t('signup.bizBenefits')}</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• {t('signup.benefit1')}</li>
                        <li>• {t('signup.benefit2')}</li>
                        <li>• {t('signup.benefit3')}</li>
                        <li>• {t('signup.benefit4')}</li>
                      </ul>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">{t('signup.bizInfo')}</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="businessName">{t('signup.bizName')}</Label>
                          <Input
                            id="businessName"
                            data-testid="business-name-input"
                            placeholder={t('signup.bizNamePh')}
                            value={businessForm.businessName}
                            onChange={(e) => setBusinessForm(prev => ({ ...prev, businessName: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="adminPhone">{t('signup.adminPhone')}</Label>
                          <PhoneInput
                            id="adminPhone"
                            value={businessForm.adminPhoneNumber}
                            onChange={(e164) => setBusinessForm(prev => ({ ...prev, adminPhoneNumber: e164 }))}
                          />
                          <p className="text-xs text-gray-500">
                            {t('signup.adminPhoneHelper')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">{t('signup.adminDetails')}</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="adminName">{t('signup.adminName')}</Label>
                          <Input
                            id="adminName"
                            data-testid="admin-name-input"
                            placeholder={t('signup.adminNamePh')}
                            value={businessForm.adminName}
                            onChange={(e) => setBusinessForm(prev => ({ ...prev, adminName: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="adminEmail">{t('signup.adminEmail')}</Label>
                          <Input
                            id="adminEmail"
                            data-testid="admin-email-input"
                            type="email"
                            placeholder={t('signup.adminEmailPh')}
                            value={businessForm.adminEmail}
                            onChange={(e) => setBusinessForm(prev => ({ ...prev, adminEmail: e.target.value }))}
                            required
                          />
                          <p className="text-xs text-gray-500">{t('signup.adminEmailHelper')}</p>
                        </div>
                      </div>
                    </div>

                    <Button type="submit" className="w-full h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                      {t('signup.createBiz')}
                    </Button>
                  </form>
                )}

                {step === 2 && (
                  <div className="text-center space-y-6">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                      <Phone className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{t('signup.verifyAdminPhone')}</h3>
                      <p className="text-gray-600 mb-4">
                        {t('signup.sentCode', { phone: businessForm.adminPhoneNumber })}
                      </p>

                      <form onSubmit={handleBusinessOTPVerification} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="adminOtp">{t('signup.enter6')}</Label>
                          <Input
                            id="adminOtp"
                            data-testid="otp-code-input"
                            placeholder={t('signup.otpPh')}
                            maxLength="6"
                            value={businessForm.adminOtpCode}
                            onChange={(e) => setBusinessForm(prev => ({ 
                              ...prev, 
                              adminOtpCode: e.target.value.replace(/\D/g, '') 
                            }))}
                            className="text-center text-lg tracking-widest"
                            required
                          />
                        </div>
                        
                        <Button 
                          type="submit" 
                          disabled={isSubmitting || businessForm.adminOtpCode.length !== 6}
                          className="w-full bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700"
                        >
                          {isSubmitting ? t('auth.verifying') : t('auth.verifyContinue')}
                        </Button>
                      </form>

                      <div className="space-y-2">
                        <Button
                          onClick={handleResendOTP}
                          variant="ghost"
                          disabled={otpTimer > 0}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          {otpTimer > 0 ? t('signup.resendIn', { time: formatTime(otpTimer) }) : t('auth.resendOtp')}
                        </Button>
                        <Button
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="w-full"
                        >
                          {t('signup.changePhone')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <form onSubmit={handleProfileCompletion} className="space-y-6">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-medium text-yellow-900">{t('signup.completeProfile')}</h4>
                      </div>
                      <p className="text-sm text-yellow-800 mt-1">
                        {t('signup.completeProfileHint')}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">{t('signup.contactInfo')}</h4>
                      <div className="space-y-2">
                        <Label htmlFor="businessPhone">{t('signup.bizPhone')}</Label>
                        <PhoneInput
                          id="businessPhone"
                          value={businessForm.businessPhone}
                          onChange={(e164) => setBusinessForm(prev => ({ ...prev, businessPhone: e164 }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">{t('signup.industryClass')}</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="businessSector">{t('signup.bizSector')}</Label>
                          <Select 
                            value={businessForm.businessSector || undefined} 
                            onValueChange={(value) => {
                              setBusinessForm(prev => ({ 
                                ...prev, 
                                businessSector: value, 
                                businessSubSector: '' 
                              }))
                            }}
                          >
                            <SelectTrigger data-testid="business-sector-select">
                              <SelectValue placeholder={t('signup.selectSector')} />
                            </SelectTrigger>
                            <SelectContent>
                              {businessSectors.map((sector) => (
                                <SelectItem key={sector} value={sector}>
                                  {sector}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="businessSubSector">{t('signup.bizSubSector')}</Label>
                          <Select 
                            value={businessForm.businessSubSector || undefined}
                            onValueChange={(value) => setBusinessForm(prev => ({ ...prev, businessSubSector: value }))}
                            disabled={!businessForm.businessSector}
                          >
                            <SelectTrigger data-testid="business-sub-sector-select">
                              <SelectValue placeholder={t('signup.selectSubSector')} />
                            </SelectTrigger>
                            <SelectContent>
                              {businessForm.businessSector && businessSubSectors[businessForm.businessSector]?.map((subSector) => (
                                <SelectItem key={subSector} value={subSector}>
                                  {subSector}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">{t('signup.regDetails')}</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="registrationNumber">{t('signup.regNumber')}</Label>
                          <Input
                            id="registrationNumber"
                            data-testid="registration-number-input"
                            placeholder={t('signup.regNumberPh')}
                            value={businessForm.registrationNumber}
                            onChange={(e) => setBusinessForm(prev => ({ ...prev, registrationNumber: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="registrationCountry">{t('signup.regCountry')}</Label>
                          <Select 
                            value={businessForm.registrationCountry || undefined}
                            onValueChange={(value) => setBusinessForm(prev => ({ ...prev, registrationCountry: value }))}
                          >
                            <SelectTrigger data-testid="registration-country-select">
                              <SelectValue placeholder={t('signup.selectCountry')} />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem key={country} value={country}>
                                  {country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      data-testid="continue-to-tier-btn"
                      className="w-full h-12 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                    >
                      {t('signup.completeBizProfile')}
                    </Button>
                  </form>
                )}

                {step === 4 && (
                  <TierPicker
                    tiers={liveTiers}
                    selected={businessForm.subscriptionTier}
                    onSelect={(tierId) => setBusinessForm(prev => ({ ...prev, subscriptionTier: tierId }))}
                    onContinueToPayment={handleSubscriptionSelection}
                    onStartTrial={handleStartTrial}
                    isSubmitting={isSubmitting}
                    trialPending={trialPending}
                  />
                )}

                {step === 5 && (
                  <div className="text-center space-y-6">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{t('signup.regComplete')}</h3>
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
                        <h4 className="font-medium text-green-900 mb-2">{t('signup.yourBrandId')}</h4>
                        <div className="text-2xl font-bold text-green-700 tracking-wider">
                          {generateBrandId()}
                        </div>
                        <p className="text-sm text-green-600 mt-2">
                          {t('signup.shareBrandId')}
                        </p>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h4 className="font-medium text-blue-900 mb-2">{t('signup.subscriptionPlan')}</h4>
                        <div className="flex items-center justify-center space-x-2">
                          <Badge className={(liveTiersById[businessForm.subscriptionTier] || {}).color || 'bg-purple-100 text-purple-800'}>
                            {(liveTiersById[businessForm.subscriptionTier] || {}).name || businessForm.subscriptionTier}
                          </Badge>
                          {(liveTiersById[businessForm.subscriptionTier] || {}).price > 0 && (
                            <span className="text-sm text-blue-600">
                              ${liveTiersById[businessForm.subscriptionTier].price}/{liveTiersById[businessForm.subscriptionTier].interval}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-4">
                        Your business account has been created successfully with {(liveTiersById[businessForm.subscriptionTier] || {}).name || ''} plan. 
                        Staff members can now register using your Brand ID.
                      </p>
                      <Button
                        onClick={() => navigate('/login')}
                        className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                      >
                        {t('signup.goToLogin')}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignUp;